import React from 'react';
import { useTranslation } from 'react-i18next';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/response/TireSearchResponse';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import Filter from 'components/Filter';
import { RangeFacetLocal, TextFacetLocal } from 'components/Filter/Filter.types';
import { SortData } from 'components/Sort/Sort.types';
import { TIRE } from 'domains/catalog/Catalog.types';
import { TireSortReference } from 'domains/tires/Tire.types';
import { SparePartsViewType } from 'domains/user';
import { TireReferenceCardsContainer } from 'pages/TiresPage/TireReferencesSection/TireReferencesContainer/TireReferenceCardsContainer';
import { Box, CenteredSpin, CenterFlex, Flex, MarginBox } from 'UI';
import { SEARCH_STATUS } from 'utils';

const TireReferencesLoader = () => (
  <CenterFlex>
    <Box height={200}>
      <CenteredSpin />
    </Box>
  </CenterFlex>
);

const TireReferencesError = () => {
  const { t } = useTranslation();
  return (
    <CenterFlex>
      <Box height={210}>
        <ErrorWithLabel
          label={t(
            'catalog.tires.references.backend_error',
            'References for the given tire parameters are temporarily unavailable, please try again later.',
          )}
        />
      </Box>
    </CenterFlex>
  );
};

const TireReferenceSections = ({
  exactMatchTires,
  nonExactMatchTires,
  sparePartsView,
  compact,
  sort,
  t,
}: {
  exactMatchTires: TireItem[];
  nonExactMatchTires: TireItem[];
  sparePartsView: SparePartsViewType;
  compact: boolean;
  sort: SortData<TireSortReference>[];
  t: ReturnType<typeof useTranslation>['t'];
}) => (
  <Flex direction={'column'}>
    {exactMatchTires.length > 0 && (
      <TireReferenceCardsContainer
        tires={exactMatchTires}
        sparePartsView={sparePartsView}
        singleSection={nonExactMatchTires.length === 0}
        initShowCount={5}
        isNoLongerAvailable={false}
        compact={compact}
        sort={sort}
      />
    )}
    {nonExactMatchTires.length > 0 && (
      <TireReferenceCardsContainer
        title={t('catalog.tires.similar.products.info.msg', 'These products could also match your needs')}
        tires={nonExactMatchTires}
        sparePartsView={sparePartsView}
        singleSection={exactMatchTires.length === 0}
        initShowCount={4}
        isNoLongerAvailable={false}
        compact
        sort={sort}
      />
    )}
  </Flex>
);

export interface TireReferencesViewProps {
  direction: 'row' | 'column';
  margin: number;
  searchStatus: SEARCH_STATUS | undefined;
  exactMatchTires: TireItem[];
  nonExactMatchTires: TireItem[];
  sparePartsView: SparePartsViewType;
  compact: boolean;
  sort: SortData<TireSortReference>[];
  t: ReturnType<typeof useTranslation>['t'];
  filters: {
    textFilters: Map<string, string[]>;
    rangeFilters: Map<string, { min: number; max: number }>;
  };
  textFilters: TextFacetLocal[];
  rangeFilters: RangeFacetLocal[];
  isFilterLoading: boolean;
  showPriceless: boolean;
  setShowPriceless: React.Dispatch<React.SetStateAction<boolean>>;
  onSetTextFilters: (id: string, item: string) => void;
  onSetRangeFilters: (id: string, range?: { min: number; max: number }) => void;
  onResetFilters: () => void;
  filtersMapType: typeof TIRE;
  filtersMap: Map<string, { label: string; ids: string[] }>;
}

export const TireReferencesView = ({
  direction,
  margin,
  searchStatus,
  exactMatchTires,
  nonExactMatchTires,
  sparePartsView,
  compact,
  sort,
  t,
  filters,
  textFilters,
  rangeFilters,
  isFilterLoading,
  showPriceless,
  setShowPriceless,
  onSetTextFilters,
  onSetRangeFilters,
  onResetFilters,
  filtersMapType,
  filtersMap,
}: TireReferencesViewProps) => {
  return (
    <Flex direction={direction}>
      <Flex direction={'column'} size={0}>
        <MarginBox ml={margin} my={margin}>
          <Filter
            filters={filters}
            textFilters={textFilters}
            rangeFilters={rangeFilters}
            setTextFilters={onSetTextFilters}
            setRangeFilters={onSetRangeFilters}
            resetFilters={onResetFilters}
            loading={isFilterLoading}
            facetsMap={filtersMap}
            usePrice={{
              showPriceless,
              setShowPriceless,
            }}
            requiredMin={0}
            type={filtersMapType}
          />
        </MarginBox>
      </Flex>
      <DataContainer data={searchStatus} Loading={TireReferencesLoader} ErrorState={TireReferencesError}>
        <TireReferenceSections
          exactMatchTires={exactMatchTires}
          nonExactMatchTires={nonExactMatchTires}
          sparePartsView={sparePartsView}
          compact={compact}
          sort={sort}
          t={t}
        />
      </DataContainer>
    </Flex>
  );
};
